import React from 'react';
import { AlertTriangle, PhoneCall, MapPin, Clock, ShieldCheck, Star } from 'lucide-react';

const Emergency = () => {
  const vets = [
    {
      name: 'Dr. S. Kavitha Rajan',
      clinic: 'Paws & Care Veterinary Hospital',
      specialty: 'Small Animal Surgery & Emergency Care',
      qual: 'BVSc & AH, MVSc – Tamil Nadu Veterinary College',
      area: 'RS Puram',
      address: '18, Swamy Vivekananda Road, RS Puram, Coimbatore – 641002',
      distance: '1.2 km',
      available: '24/7',
      rating: '4.9',
      reviews: 214,
      phone: '+91 98421 11290',
      badge: 'Top Rated',
      badgeColor: 'bg-green-100 text-green-700',
    },
    {
      name: 'Dr. M. Arjun Krishnan',
      clinic: 'CityPet Multispeciality Clinic',
      specialty: 'Emergency & Critical Care · Dogs & Cats',
      qual: 'BVSc & AH – TNAU, Coimbatore',
      area: 'Gandhipuram',
      address: '9, Big Bazaar Street, Gandhipuram, Coimbatore – 641009',
      distance: '2.8 km',
      available: '24/7',
      rating: '4.8',
      reviews: 189,
      phone: '+91 94433 09812',
      badge: 'Emergency Specialist',
      badgeColor: 'bg-red-100 text-red-700',
    },
    {
      name: 'Dr. Priya Selvam',
      clinic: 'Happy Pets Veterinary Clinic',
      specialty: 'Internal Medicine · Vaccination · Dentistry',
      qual: 'BVSc & AH – Madurai Veterinary College',
      area: 'Saibaba Colony',
      address: '42, Mettupalayam Road, Saibaba Colony, Coimbatore – 641011',
      distance: '3.5 km',
      available: 'Mon–Sat: 9 AM – 10 PM',
      rating: '4.7',
      reviews: 97,
      phone: '+91 93840 05577',
      badge: 'Highly Recommended',
      badgeColor: 'bg-blue-100 text-blue-700',
    },
    {
      name: 'Dr. R. Senthilkumar',
      clinic: 'Coimbatore Animal Care Centre',
      specialty: 'Orthopedics · Dermatology · Exotic Animals',
      qual: 'MVSc – Veterinary Surgery, TNAU',
      area: 'Peelamedu',
      address: '77, Avinashi Road, Peelamedu, Coimbatore – 641004',
      distance: '4.2 km',
      available: 'Daily: 8 AM – 11 PM',
      rating: '4.8',
      reviews: 133,
      phone: '+91 96000 34221',
      badge: 'Exotic Specialist',
      badgeColor: 'bg-purple-100 text-purple-700',
    },
  ];

  return (
    <section id="emergency" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 py-1.5 mb-4">
            <AlertTriangle size={14} className="text-emergency-red animate-pulse" />
            <span className="text-emergency-red font-bold text-sm">Emergency Services</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Licensed Vets Available <br/>
            <span className="text-emergency-red">Across Coimbatore &amp; South India</span>
          </h2>
          <p className="text-xl text-gray-500">
            When every minute counts, contact our verified vets directly.
            We are starting in Coimbatore and expanding to all major South Indian cities.
          </p>
        </div>

        {/* SOS Banner */}
        <div className="bg-gradient-to-r from-emergency-red to-red-600 rounded-3xl p-8 mb-12 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl shadow-red-100">
          <div className="text-center md:text-left">
            <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2">Pet in danger right now?</h3>
            <p className="text-red-100 text-lg">Call our emergency hotline — a vet will guide you immediately.</p>
          </div>
          <a
            href="tel:+919842111290"
            className="flex items-center gap-3 bg-white text-emergency-red font-extrabold text-xl px-8 py-4 rounded-2xl shadow-lg hover:bg-red-50 transition-all transform hover:scale-105 whitespace-nowrap"
          >
            <PhoneCall size={24} /> SOS: +91 98421 11290
          </a>
        </div>

        {/* Vet Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {vets.map((vet, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{vet.name}</h3>
                  <p className="text-brand-teal font-semibold text-sm mt-0.5">{vet.clinic}</p>
                </div>
                <span className={`text-xs font-bold px-3 py-1 rounded-full flex-shrink-0 ml-3 ${vet.badgeColor}`}>{vet.badge}</span>
              </div>

              <p className="text-emergency-red text-sm font-medium mb-1">{vet.specialty}</p>
              <p className="text-gray-400 text-xs mb-4">{vet.qual}</p>

              <div className="space-y-2 mb-5 text-sm text-gray-500">
                <div className="flex items-start gap-2">
                  <MapPin size={15} className="text-brand-teal flex-shrink-0 mt-0.5" />
                  <span>{vet.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={15} className="text-brand-orange" />
                  <span>{vet.available}</span>
                  <span className="text-gray-300">|</span>
                  <MapPin size={13} className="text-gray-300" />
                  <span>{vet.distance}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star size={14} className="text-yellow-400" fill="currentColor" />
                  <span className="font-semibold text-gray-800">{vet.rating}</span>
                  <span className="text-gray-400 text-xs">({vet.reviews} patients)</span>
                </div>
              </div>

              <a
                href={`tel:${vet.phone}`}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-emergency-soft text-emergency-red font-bold border border-red-100 hover:bg-emergency-red hover:text-white transition-colors"
              >
                <PhoneCall size={17} /> {vet.phone}
              </a>
            </div>
          ))}
        </div>

        {/* Trust badge */}
        <div className="flex items-center justify-center gap-2 mt-10 text-sm text-gray-400">
          <ShieldCheck size={16} className="text-brand-teal" />
          All veterinarians hold valid State Veterinary Council registration · Expanding across South India
        </div>

      </div>
    </section>
  );
};

export default Emergency;
